// src/prototype/components/sep/SEPSectionExecutiveSummary.tsx
// execute_3 alignment pass — March 2026:
//   - Section header aligned to OIR standard (text-base font-semibold, no coral bar, no "Section N")
//   - Narrative text color: text-[#4A5568] throughout
//   - KPI card labels aligned to confirmed set: Portfolio ROI Range, Initiatives Selected,
//     Execution Horizon, Quick Win Count
//   - FindingRow text color aligned to body standard

import React from 'react';
import { TrendingUp, Layers, Zap, Clock, CheckCircle2, CalendarDays } from 'lucide-react';
import { effortTierForBadge, effortLabelForBadge, isQuickWin } from '../../config/opportunityBadge';

interface Finalist {
  id: string;
  rank: number;
  title: string;
  category: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  roiEstimate: { low: number; high: number; unit: string };
  validationScore: number;
}

export interface SEPSectionExecutiveSummaryProps {
  clientName: string;
  finalists: Finalist[];
  portfolioRoiLow: number;
  portfolioRoiHigh: number;
  executionHorizon: string;
  quickWinCount: number;
}

const effortChip = (effort: string) =>
  ({ Low: 'bg-emerald-50 text-emerald-700 border border-emerald-100', Medium: 'bg-amber-50 text-amber-700 border border-amber-100', High: 'bg-rose-50 text-rose-600 border border-rose-100' })[effort] ?? 'bg-gray-100 text-gray-600 border border-gray-200';

// Chip and label read the ROOT quadrant/feasibility for the row, not its stored effort string.
const badgeInput = (f: { id?: string; implementationEffort: string }) => ({ id: f.id, implementationEffort: f.implementationEffort });

const MetricCard: React.FC<{ value: string; label: string; sub?: string; accent?: boolean; icon: React.ReactNode }> = ({ value, label, sub, accent, icon }) => (
  <div className={`rounded-xl p-4 flex flex-col gap-1.5 ${accent ? 'bg-[#FF6E4C]/[0.08] border border-[#FF6E4C]/20' : 'bg-[#18365E]/[0.05] border border-[#18365E]/10'}`}>
    <div className="flex items-center gap-1.5">
      <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${accent ? 'bg-[#FF6E4C]/15' : 'bg-[#18365E]/10'}`}>{icon}</div>
      <span className="text-[9px] font-semibold text-[#717182] uppercase tracking-wide leading-tight">{label}</span>
    </div>
    <p className={`text-2xl font-bold leading-none ${accent ? 'text-[#FF6E4C]' : 'text-[#18365E]'}`}>{value}</p>
    {sub && <p className="text-[10px] text-[#717182] leading-tight">{sub}</p>}
  </div>
);

const FindingRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <CheckCircle2 className="w-4 h-4 text-[#FF6E4C] shrink-0 mt-px" />
    <p className="text-sm text-[#4A5568] leading-relaxed">{children}</p>
  </div>
);

const InitiativeCard: React.FC<{ finalist: Finalist; index: number }> = ({ finalist, index }) => (
  <div className="border border-[#e5e7eb] rounded-xl p-4 bg-white flex flex-col gap-3 hover:shadow-sm transition-shadow">
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-full bg-[#18365E] flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-[11px] font-bold text-white">{index + 1}</span>
      </div>
      <p className="text-sm font-semibold text-[#18365E] leading-snug">{finalist.title}</p>
    </div>
    <div className="flex items-baseline gap-1.5">
      <TrendingUp className="w-3.5 h-3.5 text-[#FF6E4C] shrink-0" />
      <span className="text-base font-bold text-[#FF6E4C]">${finalist.roiEstimate.low}–${finalist.roiEstimate.high}{finalist.roiEstimate.unit}</span>
      <span className="text-[10px] text-[#717182]">/yr</span>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${effortChip(effortTierForBadge(badgeInput(finalist)))}`}>
        {isQuickWin(badgeInput(finalist)) && <Zap className="w-2.5 h-2.5 inline-block mr-0.5 -mt-px" />}
        {effortLabelForBadge(badgeInput(finalist))}
      </span>
      <div className="flex items-center gap-1 text-[10px] text-[#717182]">
        <Clock className="w-2.5 h-2.5 shrink-0" /><span>{finalist.timeToValue}</span>
      </div>
    </div>
  </div>
);

export const SEPSectionExecutiveSummary: React.FC<SEPSectionExecutiveSummaryProps> = ({
  clientName, finalists, portfolioRoiLow, portfolioRoiHigh, executionHorizon, quickWinCount,
}) => {
  const roiLabel = `$${portfolioRoiLow.toFixed(1)}–$${portfolioRoiHigh.toFixed(1)}M`;
  const topThree = finalists.slice(0, 3);

  return (
    <div id="sep-exec-summary" className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-6 mb-6" style={{ scrollMarginTop: '88px' }}>

      {/* Section header — OIR standard */}
      <div className="mb-4">
        <h2 className="text-base font-semibold text-[#18365E]">Executive Summary</h2>
        <p className="text-xs text-[#717182] mt-0.5">Portfolio value, execution horizon, and selected initiatives overview</p>
      </div>

      {/* Narrative */}
      <p className="text-sm text-[#4A5568] leading-relaxed mb-6">
        This Strategic Execution Plan presents the approved initiative portfolio for{' '}
        <strong className="text-[#18365E]">{clientName}</strong>, translating the evaluated opportunity landscape into a
        structured execution strategy. The plan sequences initiatives by value delivery timeline,
        defines resource requirements, and establishes the governance framework for leadership approval.
      </p>

      {/* KPI cards — confirmed set */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <MetricCard
          icon={<TrendingUp className="w-3.5 h-3.5 text-[#FF6E4C]" />}
          value={roiLabel}
          label="Portfolio ROI Range"
          sub="Combined annual value potential"
          accent
        />
        <MetricCard
          icon={<Layers className="w-3.5 h-3.5 text-[#18365E]" />}
          value={String(finalists.length)}
          label="Initiatives Selected"
          sub="Approved for execution planning"
        />
        <MetricCard
          icon={<CalendarDays className="w-3.5 h-3.5 text-[#18365E]" />}
          value={executionHorizon}
          label="Execution Horizon"
          sub="Full portfolio delivery window"
        />
        <MetricCard
          icon={<Zap className="w-3.5 h-3.5 text-[#FF6E4C]" />}
          value={String(quickWinCount)}
          label="Quick Win Count"
          sub="Low complexity · fast ROI"
          accent={quickWinCount > 0}
        />
      </div>

      {/* Execution readiness findings */}
      <div className="mb-6">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Execution Readiness Summary</p>
        <div className="space-y-2.5 bg-[#F5F7FA] rounded-xl p-4 border border-[#e5e7eb]">
          <FindingRow>
            The approved execution portfolio represents <strong>{roiLabel}</strong> in combined annual value across <strong>{finalists.length}</strong> prioritized initiatives.
          </FindingRow>
          <FindingRow>
            {quickWinCount > 0
              ? <><strong>{quickWinCount}</strong> Quick Win initiative{quickWinCount !== 1 ? 's' : ''} can be launched immediately with lower complexity and faster time-to-value.</>
              : <>All selected initiatives have been assessed for strategic fit, ROI confidence, and implementation readiness.</>}
          </FindingRow>
          <FindingRow>
            Sequenced execution across a <strong>{executionHorizon}</strong> horizon enables value delivery at multiple milestones while managing organizational capacity.
          </FindingRow>
          <FindingRow>
            Each initiative has confirmed ROI assumptions, a defined resource model, and approval-ready financial documentation to support leadership sign-off.
          </FindingRow>
        </div>
      </div>

      {/* Selected initiatives */}
      <div>
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Selected Initiatives</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {topThree.map((f, i) => <InitiativeCard key={f.id} finalist={f} index={i} />)}
        </div>
      </div>

    </div>
  );
};

export default SEPSectionExecutiveSummary;